import { observable } from "@legendapp/state";
import {
  listAllExternalUsers,
  listExternalUsersByAgency,
  searchExternalUsers,
} from "@/apis/clients/externalUsersClient";
import { toErrorMessage } from "@/stores/storeHelpers";
import { TEXT } from "@/constants/text";
import type { ExternalUser } from "@/types/externalUser";

/** What the current list was asked for, so a refetch can repeat it. */
type Query =
  | { kind: "all" }
  | { kind: "agency"; agencyId: number }
  | { kind: "search"; text: string };

interface ExternalUsersState {
  users: ExternalUser[];
  isLoading: boolean;
  error: string | null;
  query: Query | null;
}

const defaultValues: ExternalUsersState = {
  users: [],
  isLoading: false,
  error: null,
  query: null,
};

export const ExternalUsersStore$ = observable(defaultValues);

/** Test seam. */
export const resetExternalUsers = () => ExternalUsersStore$.set(defaultValues);

/**
 * Identifies the most recently started request, so a response can tell
 * whether it is still the one the screen is waiting on.
 *
 * Two requests started close together — a search fired while an agency load
 * is still in flight, or two agency switches in quick succession — resolve
 * in whichever order their responses arrive, not the order they were sent.
 * Comparing `Query` values can't tell newest from stale, because two rapid
 * switches to the same agency produce equal queries; a counter captured at
 * the start of each `run()` call can.
 */
let latestRequestId = 0;

const run = async (query: Query) => {
  latestRequestId += 1;
  const requestId = latestRequestId;
  ExternalUsersStore$.assign({ isLoading: true, error: null, query });
  try {
    let users;
    if (query.kind === "all") {
      users = await listAllExternalUsers();
    } else if (query.kind === "agency") {
      users = await listExternalUsersByAgency(query.agencyId);
    } else {
      users = await searchExternalUsers(query.text);
    }
    // A superseded request's response is discarded outright: writing its
    // users, its error, or even clearing isLoading would all overwrite state
    // a newer, still-in-flight or already-settled request owns.
    if (requestId !== latestRequestId) return;
    ExternalUsersStore$.users.set(users);
  } catch (error) {
    if (requestId !== latestRequestId) return;
    // Emptied, not left alone: rows loaded for one agency displayed under
    // another agency's name would report the wrong people as its members.
    ExternalUsersStore$.assign({
      users: [],
      error: toErrorMessage(error) || TEXT.userManagement.loadFailed,
    });
  } finally {
    if (requestId === latestRequestId) {
      ExternalUsersStore$.isLoading.set(false);
    }
  }
};

/**
 * Load every user. What the screen opens on.
 */
export const loadAllUsers = () => run({ kind: "all" });

export const loadAgencyUsers = (agencyId: number) =>
  run({ kind: "agency", agencyId });

export const searchUsers = (text: string) => run({ kind: "search", text });

/**
 * Repeat the last query.
 *
 * Every mutation refetches, because a mutation response carries only the
 * fields this service owns — the name, role and agency on a row come from T1
 * and can only be had by asking again.
 */
export const reloadCurrent = async () => {
  const query = ExternalUsersStore$.query.get();
  if (query) await run(query);
};
